"use client";

import { Fragment, useState } from "react";
import Link from "next/link";
import { ChevronDown, Store } from "lucide-react";
import { ShortLGS } from "modules/common/model/models";
import { useSharedStores } from "modules/lgs/hooks/useSharedStores";
import { cn } from "utils/utils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

interface StoreChipsProps {
  stores: ShortLGS[];
  label: string;
  limit?: number;
  /** Highlight stores the signed-in viewer also goes to (sorted first). */
  highlightShared?: boolean;
  /** Profile owner's user id — highlighting is skipped on your own profile. */
  ownerId?: string;
  /**
   * "sm": badge chips (rail, store pages).
   * "md": btn-sm buttons, shared stores split into their own column (profile
   *       header). Profile-only — nothing else asks for "md".
   * "list": one store per row (roster cards) — height tracks the item count
   *         instead of how the names happen to wrap. Capped by `limit` like the
   *         rest: these sit in a grid whose row height follows the tallest
   *         card, so an uncapped list stretches every neighbour.
   * "inline": one dot-separated text line (chat header).
   * "profile": wrapping plain-text links for a labeled profile detail row.
   * "strip": a tinted one-sentence band naming a single store (hover card).
   * "strip" ignores `limit` — it always names exactly one.
   */
  size?: "sm" | "md" | "list" | "inline" | "profile" | "strip";
  /**
   * How "inline" reports the stores past `limit`: a static `+N` with the names
   * in its title, or a dropdown of the full list. Only "count" is safe inside
   * a popover — a nested daisyUI dropdown gets clipped by the card. Ignored by
   * the other sizes, which have their own expand toggle.
   */
  overflow?: "count" | "menu";
  className?: string;
}

/**
 * Full store list, shared-first, styled as `dropdown-content`. Used by the
 * inline "+N more" dropdown and ChatHeader's mobile store trigger.
 */
export function StoreMenuList({
  stores,
  sharedIds,
  title,
}: {
  stores: ShortLGS[];
  sharedIds: Set<number>;
  title?: string;
}) {
  return (
    <ul
      tabIndex={0}
      className="dropdown-content menu bg-base-100 rounded-box border-base-content/20 z-20 mt-1 max-h-60 w-56 flex-nowrap overflow-y-auto border p-2 shadow-xl"
    >
      {title && <li className="menu-title">{title}</li>}
      {stores.map((s) => {
        const isShared = sharedIds.has(s.wotcId);
        return (
          <li key={s.wotcId}>
            <Link href={`/stores/${s.slug}`} title={s.name}>
              <Store
                size={12}
                className={cn(
                  "shrink-0",
                  isShared ? "text-primary" : "text-base-content/50"
                )}
              />
              <span className="min-w-0 flex-1 truncate">{s.name}</span>
              {isShared && (
                <span className="badge badge-soft badge-primary badge-xs">
                  Mutual
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function StoreChips({
  stores,
  label,
  limit = 2,
  highlightShared = false,
  ownerId,
  size = "sm",
  overflow = "count",
  className,
}: StoreChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const { sharedIds, sortSharedFirst } = useSharedStores({
    ownerId,
    enabled: highlightShared,
  });

  if (stores.length === 0) {
    return null;
  }

  // On public ISR pages (e.g. /users/[username]) there is no viewer at
  // render time, so highlighted chips reorder once post-hydration when the
  // viewer's store list loads. Accepted: gating the render would blank the
  // chips instead, and skipping the sort could hide a shared store behind
  // the "+N more" limit.
  const ordered = sortSharedFirst(stores);
  const shown = expanded ? ordered : ordered.slice(0, limit);
  const extra = ordered.length - shown.length;

  if (size === "strip") {
    // One whole name beats two truncated ones at this width, and shared-first
    // ordering means ordered[0] is the one worth naming.
    const top = ordered[0];
    const isShared = sharedIds.has(top.wotcId);
    const rest = ordered.slice(1);

    return (
      <div
        className={cn(
          "bg-base-200 rounded-box flex items-center gap-2 px-2.5 py-2 text-xs",
          className
        )}
      >
        <Store
          size={13}
          className={cn(
            "shrink-0",
            isShared ? "text-primary" : "text-base-content/40"
          )}
        />
        <span className="text-base-content/60 min-w-0 truncate">
          {isShared ? "You both trade at" : label}{" "}
          <span className="text-base-content font-semibold">{top.name}</span>
        </span>
        {rest.length > 0 && (
          <span
            className="text-base-content/40 ml-auto shrink-0 tabular-nums"
            title={rest
              .map((s) =>
                sharedIds.has(s.wotcId) ? `${s.name} (mutual)` : s.name
              )
              .join(", ")}
          >
            +{rest.length}
          </span>
        )}
      </div>
    );
  }

  // The shared stores get their own titled column instead of a per-button
  // "Shared" addon. ponytail: recursing with the two subsets reuses the whole
  // renderer — each child owns its own expand toggle and neither refetches nor
  // re-sorts. `highlightShared={false}` on the children is load-bearing: it's
  // the only thing keeping this branch from recursing into itself forever.
  if (size === "md" && highlightShared) {
    const mutual = ordered.filter((s) => sharedIds.has(s.wotcId));
    if (mutual.length > 0) {
      const rest = ordered.filter((s) => !sharedIds.has(s.wotcId));
      return (
        <div
          className={cn(
            "flex flex-col items-start gap-4 md:flex-row md:gap-6",
            className
          )}
        >
          {rest.length > 0 && (
            <StoreChips
              stores={rest}
              label={label}
              limit={limit}
              size="md"
              highlightShared={false}
            />
          )}
          <StoreChips
            stores={mutual}
            label="Mutual"
            limit={limit}
            size="md"
            highlightShared={false}
            // Divider only when this column actually follows another one.
            className={cn(
              rest.length > 0 && "md:border-base-content/10 md:border-l md:pl-6"
            )}
          />
        </div>
      );
    }
  }

  if (size === "inline") {
    // No stacked header and no expand toggle, so it can't grow its row.
    return (
      <div
        className={cn(
          "text-base-content/60 flex min-w-0 items-center gap-1 text-xs",
          className
        )}
      >
        <Store size={12} className="text-primary shrink-0" aria-hidden="true" />
        <span className="shrink-0">{label}</span>
        {shown.map((s, i) => {
          const isShared = sharedIds.has(s.wotcId);
          // `truncate` works directly on the link because flex items are
          // blockified, so its overflow:hidden applies and the item shrinks.
          // max-w caps one long name, min-w-0 lets several share the row.
          const nameClass = cn(
            "min-w-0 max-w-[8.5rem] truncate",
            isShared ? "text-primary font-semibold" : "font-medium"
          );
          const title = isShared ? `You both trade at ${s.name}` : s.name;
          return (
            <Fragment key={s.wotcId}>
              {i > 0 && (
                <span className="text-base-content/40 shrink-0">·</span>
              )}
              {s.slug ? (
                <Link
                  href={`/stores/${s.slug}`}
                  className={cn(
                    nameClass,
                    "hover:underline",
                    !isShared && "hover:text-base-content"
                  )}
                  title={title}
                >
                  {s.name}
                </Link>
              ) : (
                <span className={nameClass} title={title}>
                  {s.name}
                </span>
              )}
            </Fragment>
          );
        })}
        {extra > 0 &&
          (overflow === "menu" ? (
            <div className="dropdown dropdown-end shrink-0">
              <button
                type="button"
                tabIndex={0}
                className="badge badge-sm badge-soft border-base-content/15 bg-base-200 text-base-content/70 hover:border-base-content/40 hover:text-base-content gap-0.5 font-medium tabular-nums"
                aria-haspopup="menu"
                aria-label={`Show all ${ordered.length} stores`}
              >
                +{extra} more
                <ChevronDown size={12} className="shrink-0" />
              </button>
              <StoreMenuList stores={ordered} sharedIds={sharedIds} />
            </div>
          ) : (
            <span
              className="text-base-content/40 shrink-0 tabular-nums"
              title={ordered
                .slice(shown.length)
                .map((s) => s.name)
                .join(", ")}
            >
              +{extra}
            </span>
          ))}
      </div>
    );
  }

  if (size === "profile") {
    return (
      <div
        className={cn(
          "text-base-content/75 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-sm",
          className
        )}
      >
        {shown.map((s) => {
          const isShared = sharedIds.has(s.wotcId);
          const nameClass = cn("font-medium", isShared && "font-semibold");
          const title = isShared ? `You both trade at ${s.name}` : s.name;

          return s.slug ? (
            <Link
              key={s.wotcId}
              href={`/stores/${s.slug}`}
              className={cn(nameClass, "link link-primary link-hover")}
              title={title}
            >
              {s.name}
            </Link>
          ) : (
            <span
              key={s.wotcId}
              className={cn(nameClass, "text-base-content/75")}
              title={title}
            >
              {s.name}
            </span>
          );
        })}
        {extra > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-base-content/60 hover:text-base-content font-medium tabular-nums hover:underline"
            aria-label={`Show ${extra} more store${extra === 1 ? "" : "s"}`}
          >
            +{extra} more
          </button>
        )}
        {expanded && stores.length > limit && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-base-content/50 hover:text-base-content font-medium hover:underline"
            aria-label="Collapse stores"
          >
            Show less
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span
        className={cn(
          "text-base-content/50 text-xs font-bold tracking-wider",
          size === "list" && "flex items-center gap-1.5"
        )}
      >
        {/* list: one icon for the section, standing in for the per-row ones */}
        {size === "list" && (
          <Store size={14} className="text-primary shrink-0" />
        )}
        {label}
      </span>
      <div
        className={cn(
          size === "list"
            ? "flex flex-col items-start gap-1"
            : "flex flex-wrap items-center gap-1.5"
        )}
      >
        {/* md: one leading store icon for the row instead of one per chip */}
        {size === "md" && (
          <Store size={ICON_SIZE} className="text-primary shrink-0" />
        )}
        {shown.map((s) => {
          const isShared = sharedIds.has(s.wotcId);
          const title = isShared ? "You both trade here" : s.name;

          if (size === "list") {
            // Names only — one icon on the label marks the whole section, so an
            // expanded list isn't a column of repeated store glyphs. Shared
            // still reads from the row's own colour.
            const rowClass = cn(
              "block w-full min-w-0 truncate text-sm",
              isShared ? "text-primary font-medium" : "text-base-content/70"
            );
            return s.slug ? (
              <Link
                key={s.wotcId}
                href={`/stores/${s.slug}`}
                className={cn(rowClass, "hover:underline")}
                title={title}
              >
                {s.name}
              </Link>
            ) : (
              <span key={s.wotcId} className={rowClass} title={title}>
                {s.name}
              </span>
            );
          }

          if (size === "md") {
            // Buttons (they're links) — the column title says whether these are
            // shared, so no per-button badge. Stores can arrive without a slug
            // (older API responses, the store traders endpoint) — still show
            // the name, not as a link.
            const hoverState = "hover:border-base-content/30 hover:bg-base-300";

            return s.slug ? (
              <Link
                key={s.wotcId}
                href={`/stores/${s.slug}`}
                className={cn("btn btn-sm font-medium", hoverState)}
                title={title}
              >
                <span className="max-w-[12rem] truncate">{s.name}</span>
              </Link>
            ) : (
              <span
                key={s.wotcId}
                className="btn btn-sm pointer-events-none font-medium"
                title={title}
              >
                <span className="max-w-[12rem] truncate">{s.name}</span>
              </span>
            );
          }

          const chipClass = cn(
            "badge badge-soft badge-sm gap-1 font-medium",
            isShared
              ? "badge-primary"
              : "border-base-content/15 bg-base-200 hover:border-base-content/40"
          );
          const chipBody = (
            <>
              {/* badge-primary already tints the shared chip. */}
              <Store
                size={11}
                className={cn("shrink-0", !isShared && "text-primary")}
              />
              <span className="max-w-[8.5rem] truncate">{s.name}</span>
            </>
          );
          return s.slug ? (
            <Link
              key={s.wotcId}
              href={`/stores/${s.slug}`}
              className={chipClass}
              title={title}
            >
              {chipBody}
            </Link>
          ) : (
            <span key={s.wotcId} className={chipClass} title={s.name}>
              {chipBody}
            </span>
          );
        })}
        {extra > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className={cn(
              "font-medium tabular-nums",
              size === "md"
                ? "btn btn-sm text-base-content/70 hover:text-base-content"
                : size === "list"
                  ? "text-base-content/60 hover:text-base-content text-sm hover:underline"
                  : "badge badge-soft badge-sm border-base-content/15 bg-base-200 text-base-content/70 hover:border-base-content/40 hover:text-base-content"
            )}
            aria-label={`Show ${extra} more store${extra === 1 ? "" : "s"}`}
          >
            +{extra} more
          </button>
        )}
        {expanded && stores.length > limit && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className={cn(
              "font-medium",
              size === "md"
                ? "btn btn-ghost btn-sm text-base-content/50 hover:text-base-content"
                : size === "list"
                  ? "text-base-content/50 hover:text-base-content text-sm hover:underline"
                  : "badge badge-soft badge-sm text-base-content/50 hover:text-base-content border-transparent bg-transparent"
            )}
            aria-label="Collapse stores"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}
